import { importacionOperacionesApi } from "../importacion-operaciones/importacionOperacionesApi";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const PAGOS_API_BASE = `${API_ROOT}/api/pagos`;

// Campos del preview payment-engine que necesitan los controles previos. Se envía sólo este
// subconjunto: el control no recalcula importes de la corrida.
const SNAPSHOT_FIELDS = [
  "fiservOperationId", "terminalFiserv", "merchantId", "matchStatus", "resolutionStatus",
  "paymentEligibility", "scheduleType", "requiresCommercialExceptionRule", "paymentAmountStatus", "grossAmount",
];

export const toRunSnapshot = (rows = []) => rows.map((row) => Object.fromEntries(
  SNAPSHOT_FIELDS.map((field) => [field, row[field] ?? null]),
));

const buildForm = ({ fur, txt, runId, runRows, pagosManuales }) => {
  const formData = new FormData();
  formData.append("fur", fur);
  if (txt) formData.append("txt", txt);
  if (runId && runRows) {
    formData.append("runId", runId);
    formData.append("operacionesCorrida", JSON.stringify(toRunSnapshot(runRows)));
  }
  formData.append("pagosManuales", JSON.stringify(pagosManuales || []));
  return formData;
};

const buildProcesamientoForm = ({ importacionId, runId, txt, pagosManuales }) => {
  const formData = new FormData();
  formData.append("importacionId", importacionId);
  if (runId) formData.append("runId", runId);
  if (txt) formData.append("txt", txt);
  formData.append("pagosManuales", JSON.stringify(pagosManuales || []));
  return formData;
};

const request = (path, token, options = {}) => importacionOperacionesApi.request(`${PAGOS_API_BASE}${path}`, {
  cache: "no-store",
  ...options,
  headers: { ...importacionOperacionesApi.buildHeaders(token), ...(options.headers || {}) },
});

const download = async (path, token, body) => {
  const response = await fetch(`${PAGOS_API_BASE}${path}`, {
    method: "POST", cache: "no-store", headers: importacionOperacionesApi.buildHeaders(token), body,
  });
  if (!response.ok) {
    let message = `No se pudo descargar el archivo (${response.status}).`;
    try { message = (await response.json())?.mensaje || message; } catch (_) { /* respuesta no JSON */ }
    throw new Error(message);
  }
  return response.blob();
};

const buildQrForm = ({ fechaPago, archivo, furHistorico = null, txtHistorico = null, condiciones = {} }) => {
  const formData = new FormData();
  formData.append("fechaPago", fechaPago);
  formData.append("archivo", archivo);
  formData.append("condiciones", JSON.stringify(condiciones));
  if (furHistorico) formData.append("furHistorico", furHistorico);
  if (txtHistorico) formData.append("txtHistorico", txtHistorico);
  return formData;
};

const buildSasTestForm = ({ fechaPago, archivos = [], listas = null, configuracionId = "", tasas = [], pagosManuales = [], txtHistorico = null, feriados = [] }) => {
  const formData = new FormData();
  formData.append("fechaPago", fechaPago);
  archivos.forEach((archivo) => formData.append("archivos", archivo));
  if (listas) formData.append("listas", listas);
  if (configuracionId) formData.append("configuracionId", configuracionId);
  formData.append("tasas", JSON.stringify(tasas));
  formData.append("pagosManuales", JSON.stringify(pagosManuales));
  if (txtHistorico) formData.append("txtHistorico", txtHistorico);
  if (feriados.length) formData.append("feriados", feriados.join(","));
  return formData;
};

export const pagosApi = {
  async analizarControlesPrevios(payload, token) {
    return request("/controles-previos/analizar", token, { method: "POST", body: buildForm(payload) });
  },

  // Liquidaciones persistidas: sólo responden en el entorno de desarrollo aislado.
  async estadoLiquidaciones(token) {
    return request("/liquidaciones/estado", token);
  },

  async registrarLiquidacion({ fechaPago, ...payload }, token) {
    const formData = buildForm(payload);
    formData.append("fechaPago", fechaPago);
    return request("/liquidaciones", token, { method: "POST", body: formData });
  },

  async obtenerPendientes(token) {
    return request("/liquidaciones/obligaciones/pendientes?canal=SAS", token);
  },

  // Procesamiento SAS: lote = importación de Importación Ops + corrida de Conciliación Ops.
  async lotesSas(fecha, token) {
    return request(`/procesamiento/lotes?fecha=${encodeURIComponent(fecha)}`, token);
  },

  async procesarSas(payload, token) {
    return request("/procesamiento/controles", token, { method: "POST", body: buildProcesamientoForm(payload) });
  },

  async registrarProcesamiento(payload, token) {
    return request("/procesamiento/registrar", token, { method: "POST", body: buildProcesamientoForm(payload) });
  },

  async informarPagoManual(obligacionId, body, token) {
    return request(`/liquidaciones/obligaciones/${obligacionId}/pagos-manuales`, token, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  // Condiciones financieras (catálogo de pruebas en ZocoPagos_Dev). El porcentaje viaja como
  // texto y la simulación sólo recibe IDs: los importes se calculan en backend.
  async estadoCondiciones(token) {
    return request("/condiciones/estado", token);
  },

  async snapshotsCondiciones(token) {
    return request("/condiciones/snapshots", token);
  },

  async cargarSnapshotCondiciones(libro, token) {
    const formData = new FormData();
    formData.append("libro", libro);
    return request("/condiciones/snapshots", token, { method: "POST", body: formData });
  },

  async buscarComercios(texto, token) {
    return request(`/condiciones/comercios?q=${encodeURIComponent(texto)}`, token);
  },

  async condicionComercio(cuit, snapshotId, token) {
    const query = snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : "";
    return request(`/condiciones/comercios/${encodeURIComponent(cuit)}/condicion${query}`, token);
  },

  async listarReglas({ estado, sujeto } = {}, token) {
    const params = new URLSearchParams();
    if (estado) params.append("estado", estado);
    if (sujeto) params.append("sujeto", sujeto);
    const query = params.toString();
    return request(`/condiciones/reglas${query ? `?${query}` : ""}`, token);
  },

  async obtenerRegla(id, token) {
    return request(`/condiciones/reglas/${id}`, token);
  },

  async historialRegla(raizId, token) {
    return request(`/condiciones/reglas/${raizId}/historial`, token);
  },

  async crearRegla(body, token) {
    return request("/condiciones/reglas", token, json("POST", body));
  },

  async editarRegla(id, body, token) {
    return request(`/condiciones/reglas/${id}`, token, json("PUT", body));
  },

  async nuevaVersionRegla(raizId, body, token) {
    return request(`/condiciones/reglas/${raizId}/versiones`, token, json("POST", body));
  },

  async simularRegla(id, { importacionId, snapshotId }, token) {
    return request(`/condiciones/reglas/${id}/simular`, token, json("POST", { importacionId, snapshotId }));
  },

  async enviarRegla(id, token) {
    return request(`/condiciones/reglas/${id}/enviar`, token, { method: "POST" });
  },

  async aprobarRegla(id, comentario, token) {
    return request(`/condiciones/reglas/${id}/aprobar`, token, json("POST", { comentario }));
  },

  async rechazarRegla(id, comentario, token) {
    return request(`/condiciones/reglas/${id}/rechazar`, token, json("POST", { comentario }));
  },

  // FUR autónomo (simulación). Se envían IDs y, opcionalmente, el Diario para comparar
  // componentes; los netos y grupos se calculan en backend.
  async furCorridas(fecha, token) {
    return request(`/fur-autonomo/corridas${fecha ? `?fecha=${encodeURIComponent(fecha)}` : ""}`, token);
  },

  async furResultado(id, token) {
    return request(`/fur-autonomo/corridas/${id}`, token);
  },

  // Liquidación SAS desde archivos originales (ZIP/PDF Fiserv). Corrida temporal en ZocoPagos_Dev.
  async archivosCorridas(fecha, token) {
    return request(`/archivos/corridas${fecha ? `?fecha=${encodeURIComponent(fecha)}` : ""}`, token);
  },

  async centroLiquidaciones(token) {
    return request("/archivos/centro", token);
  },

  async archivosResultado(id, token) {
    return request(`/archivos/corridas/${id}`, token);
  },

  async archivosAuditarFechas(archivos, token) {
    const formData = new FormData();
    (archivos || []).forEach((archivo) => formData.append("archivos", archivo));
    return request("/archivos/auditoria-fechas", token, { method: "POST", body: formData });
  },

  async archivosProcesar({ fechaPago, snapshotId, archivos, resolverParametrosAutomaticamente = false, fuenteExcepciones = "CRM" }, token) {
    const formData = new FormData();
    formData.append("fechaPago", fechaPago);
    formData.append("canal", "SAS");
    if (snapshotId) formData.append("snapshotId", snapshotId);
    formData.append("resolverParametrosAutomaticamente", String(resolverParametrosAutomaticamente));
    formData.append("fuenteExcepciones", fuenteExcepciones === "AUTONOMA" ? "AUTONOMA" : "CRM");
    (archivos || []).forEach((archivo) => formData.append("archivos", archivo));
    return request("/archivos/corridas", token, { method: "POST", body: formData });
  },

  // Anticipos y excepciones sobre una corrida desde archivos. Se envían IDs y el porcentaje como
  // texto; netos, diferencias y FUR los calcula el backend.
  async anticiposPanel(corridaId, token) {
    return request(`/anticipos/corridas/${corridaId}`, token);
  },

  async anticiposProponer(corridaId, body, token) {
    return request(`/anticipos/corridas/${corridaId}/propuestas`, token, json("POST", body));
  },

  async anticiposRecalcularCorrida(corridaId, token) {
    return request(`/anticipos/corridas/${corridaId}/recalcular`, token, json("POST", {}));
  },

  async anticiposSimularHipotesisHistorica(corridaId, referenciaIds, token) {
    return request(`/anticipos/corridas/${corridaId}/hipotesis-historica`, token, json("POST", { referenciaIds }));
  },

  // Decisiones pendientes de la corrida con lo que hizo el histórico en las mismas operaciones.
  async anticiposHipotesis(corridaId, token) {
    return request(`/anticipos/corridas/${corridaId}/hipotesis`, token);
  },

  async anticiposProponerHipotesis(corridaId, body, token) {
    return request(`/anticipos/corridas/${corridaId}/hipotesis/proponer`, token, json("POST", body));
  },

  async anticiposGuardarAplicarDecision(corridaId, body, token) {
    return request(`/anticipos/corridas/${corridaId}/decisiones/guardar-aplicar`, token, json("POST", body));
  },

  // Registro autónomo de excepciones (ZocoPagos_Dev) y calendario operativo.
  async autonomiaRegistrarRetenciones(corridaId, token) {
    return request(`/autonomia/corridas/${corridaId}/excepciones/registrar`, token, json("POST", {}));
  },

  async autonomiaConfirmarReingresos(corridaId, token) {
    return request(`/autonomia/corridas/${corridaId}/excepciones/confirmar-reingresos`, token, json("POST", {}));
  },

  async autonomiaFeriados(anio, token) {
    return request(`/autonomia/feriados${anio ? `?anio=${anio}` : ""}`, token);
  },

  async decisionesOperativas(corridaId, token) {
    return request(`/autonomia/decisiones${corridaId ? `?corridaId=${encodeURIComponent(corridaId)}` : ""}`, token);
  },

  async homologaciones(token) {
    return request("/autonomia/homologaciones", token);
  },

  async homologacionDetalle(id, token) {
    return request(`/autonomia/homologaciones/${id}`, token);
  },

  async registrarHomologacion(corridaId, { txtHistorico = null, observacion = "" } = {}, token) {
    const formData = new FormData();
    if (txtHistorico) formData.append("txtHistorico", txtHistorico);
    if (observacion) formData.append("observacion", observacion);
    return request(`/autonomia/corridas/${corridaId}/homologaciones`, token, { method: "POST", body: formData });
  },

  // Vista previa del archivo bancario: controles y totales, nunca un archivo descargable.
  async vistaPreviaBancaria(corridaId, { hipotesisIds = [], pagosManuales = [], txtHistorico = null, completarConHipotesisUniformes = false }, token) {
    const formData = new FormData();
    hipotesisIds.forEach((id) => formData.append("hipotesisIds", id));
    pagosManuales.forEach((id) => formData.append("pagosManuales", id));
    formData.append("completarConHipotesisUniformes", String(completarConHipotesisUniformes));
    if (txtHistorico) formData.append("txtHistorico", txtHistorico);
    return request(`/anticipos/corridas/${corridaId}/vista-previa-bancaria`, token, { method: "POST", body: formData });
  },

  async anticiposSimular(reglaId, corridaId, token) {
    return request(`/anticipos/reglas/${reglaId}/simular`, token, json("POST", { corridaId }));
  },

  async anticiposVerificarSimulacion(reglaId, token) {
    return request(`/anticipos/reglas/${reglaId}/recalcular`, token);
  },

  async anticiposEnviar(reglaId, token) {
    return request(`/anticipos/reglas/${reglaId}/enviar`, token, json("POST", {}));
  },

  async anticiposAprobar(reglaId, comentario, token) {
    return request(`/anticipos/reglas/${reglaId}/aprobar`, token, json("POST", { comentario }));
  },

  async anticiposRechazar(reglaId, comentario, token) {
    return request(`/anticipos/reglas/${reglaId}/rechazar`, token, json("POST", { comentario }));
  },

  // Tasas del día: tablero por fecha (y corrida, para contar operaciones), copia como borrador y
  // guardado + aplicación sobre la corrida en una sola acción. Los porcentajes viajan como texto.
  async tasasDiaTablero(fecha, corridaId, token) {
    const params = new URLSearchParams({ fecha });
    if (corridaId) params.append("corridaId", corridaId);
    return request(`/tasas-dia?${params}`, token);
  },

  async tasasDiaCopiar({ origen, destino }, token) {
    return request("/tasas-dia/copiar", token, json("POST", { origen, destino }));
  },

  async tasasDiaGuardar({ fecha, corridaId, fuente, motivo, items }, token) {
    return request("/tasas-dia/guardar", token, json("POST", { fecha, corridaId, fuente, motivo, items }));
  },

  async tasasDiaEnviar({ corridaId, reglaIds }, token) {
    return request("/tasas-dia/enviar", token, json("POST", { corridaId, reglaIds }));
  },

  async tasasDiaGrupos(corridaId, token) {
    return request(`/tasas-dia/grupos?corridaId=${encodeURIComponent(corridaId)}`, token);
  },

  async tasasDiaComercios(texto, corridaId, token) {
    const params = new URLSearchParams({ q: texto });
    if (corridaId) params.append("corridaId", corridaId);
    return request(`/tasas-dia/comercios?${params}`, token);
  },

  // Cierre SAS: comparación de excepciones con el programa histórico y chequeo final (sólo lectura).
  async sasComparacionExcepciones(corridaId, token) {
    return request(`/tasas-dia/corridas/${corridaId}/excepciones-comparacion`, token);
  },

  async sasChequeoCierre(corridaId, token) {
    return request(`/tasas-dia/corridas/${corridaId}/chequeo-cierre`, token);
  },

  // Prueba SAS: las tasas viajan en la solicitud y no se guardan; no hay corrida, propuesta ni TXT.
  async pruebaSasEstado(token) {
    return request("/prueba-sas/estado", token);
  },

  async listasEstado(token) {
    return request("/listas", token);
  },

  async listasDetalle(id, token) {
    return request(`/listas/${encodeURIComponent(id)}`, token);
  },

  async listasImportar({ archivo, vigenteDesde }, token) {
    const formData = new FormData();
    formData.append("archivo", archivo);
    formData.append("vigenteDesde", vigenteDesde);
    return request("/listas/importar", token, { method: "POST", body: formData });
  },

  async listasActivar(id, { confirmoCambiosSensibles, comentario }, token) {
    return request(`/listas/${encodeURIComponent(id)}/activar`, token, json("POST", { confirmoCambiosSensibles, comentario }));
  },

  async listasRechazar(id, comentario, token) {
    return request(`/listas/${encodeURIComponent(id)}/rechazar`, token, json("POST", { comentario }));
  },

  async pruebaSasCalcular({ fechaPago, archivos = [], listas = null, configuracionId = "", tasas = [], pagosManuales = [], txtHistorico = null, feriados = [] }, token) {
    return request("/prueba-sas/calcular", token, { method: "POST", body: buildSasTestForm({ fechaPago, archivos, listas, configuracionId, tasas, pagosManuales, txtHistorico, feriados }) });
  },

  async pruebaSasFur(payload, token) {
    return download("/prueba-sas/fur", token, buildSasTestForm(payload));
  },

  async pruebaSasInforme({ fechaPago, archivos = [], feriados = [] }, token) {
    const formData = new FormData();
    formData.append("fechaPago", fechaPago);
    archivos.forEach((archivo) => formData.append("archivos", archivo));
    if (feriados.length) formData.append("feriados", feriados.join(","));
    return request("/prueba-sas/informe", token, { method: "POST", body: formData });
  },

  async pruebaQrEstado(token) {
    return request("/prueba-qr/estado", token);
  },

  async pruebaQrInforme({ fechaPago, archivo }, token) {
    const formData = new FormData();
    formData.append("fechaPago", fechaPago);
    formData.append("archivo", archivo);
    return request("/prueba-qr/informe", token, { method: "POST", body: formData });
  },

  async pruebaQrCalcular({ fechaPago, archivo, furHistorico = null, txtHistorico = null, condiciones = {} }, token) {
    return request("/prueba-qr/calcular", token, { method: "POST", body: buildQrForm({ fechaPago, archivo, furHistorico, txtHistorico, condiciones }) });
  },

  async pruebaQrFur(payload, token) {
    return download("/prueba-qr/fur", token, buildQrForm(payload));
  },

  async furSimular({ importacionId, snapshotId, diario }, token) {
    const formData = new FormData();
    formData.append("importacionId", importacionId);
    formData.append("snapshotId", snapshotId);
    if (diario) formData.append("diario", diario);
    return request("/fur-autonomo/corridas", token, { method: "POST", body: formData });
  },
};

function json(method, body) {
  return { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
}
